import { useEffect } from 'react';
import { useCanonical } from '@/hooks/useCanonical';
import { ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import BlogCard from '@/components/blog/BlogCard';
import CTASection from '@/components/CTASection';
import ReviewsSection from '@/components/ReviewsSection';
import ContactSection from '@/components/ContactSection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { blogArticles } from '@/data/blog-articles';
import { services, formules, PLANITY_URL } from '@/data/services';
import heroImg from '@/assets/hero-beauty.webp';

const stats = [
  { value: '4.5/5', label: 'Note Google' },
  { value: '8+', label: 'Soins proposés' },
  { value: '5', label: 'Expertes à votre service' },
  { value: '7j/7', label: 'Ouvert (sauf lundi)' },
];


const faqs = [
  { question: 'Les soins sont-ils adaptés aux peaux noires et métissées ?', answer: 'Oui, c\'est une spécialité de Body by CC. Carine Tchuente maîtrise les spécificités des peaux à phototypes foncés : laser Nd:YAG pour l\'épilation, peelings doux adaptés, protocoles anti-taches sécurisés. Nous accueillons toutes les carnations à Sevran.' },
  { question: 'Qu\'est-ce que le HIFU et quels résultats puis-je espérer ?', answer: 'Le HIFU est un lifting sans chirurgie par ultrasons focalisés. Il raffermit visage et corps en profondeur, avec des résultats visibles sur 2 à 3 mois et durables 12 à 18 mois. C\'est notre soin phare anti-âge à Body by CC Sevran.' },
  { question: 'Combien de séances d\'épilation laser faut-il sur peau noire ?', answer: 'Sur les peaux foncées (phototypes IV à VI), comptez 8 à 12 séances espacées de 6 à 8 semaines. Nous utilisons le laser Nd:YAG 1064nm, le seul adapté et sûr pour les peaux noires et métissées. Un test de patch est réalisé avant la première séance.' },
  { question: 'Comment prendre rendez-vous et quels sont vos horaires ?', answer: 'Rendez-vous en ligne sur bodybycc.fr, par téléphone ou directement au 65 Bd Westinghouse, 93270 Sevran. Nous sommes ouverts du mardi au dimanche de 10h à 20h (fermé le lundi). Un bilan personnalisé est inclus pour tout nouveau soin technique.' },
  { question: 'Proposez-vous des forfaits et formules avantageuses ?', answer: 'Oui : Découverte Visage, Peau Neuve, Beauté Express, Corps Sculpté et des forfaits épilation laser multi-séances. Ces formules combinent plusieurs soins complémentaires à tarif préférentiel. Contactez-nous pour un devis personnalisé.' },
];

const Index = () => {
  const recentArticles = blogArticles.slice(0, 4);

  useCanonical('/');

  useEffect(() => {
    document.title = 'Body by CC — Institut Bien-être & Esthétique à Sevran';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Institut de bien-être et esthétique à Sevran (93). HIFU, E-SLIM Cryo 360°, soins visage, corps, massages. Infirmière diplômée. Prenez rendez-vous en ligne.');
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center -mt-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Institut Body by CC — Salle de soins" className="w-full h-full object-cover" fetchPriority="high" width={1920} height={1080} />
          <div className="absolute inset-0 bg-gradient-to-r from-petrol/80 via-petrol/50 to-transparent" />
        </div>
        <div className="relative w-full px-8 md:px-16 pt-28">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] max-w-3xl"
          >
            Votre bien-être,<br />notre engagement
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed font-body"
          >
            HIFU, E-SLIM Cryo 360°, soins visage & corps à Sevran. Retrouvez confiance en vous dans un cadre chaleureux et bienveillant, encadré par une infirmière diplômée.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <a
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-accent text-accent-foreground rounded-lg px-8 py-4 font-heading font-medium text-base hover:bg-accent/90 transition-colors"
            >
              Prendre rendez-vous
            </a>
            <Link
              to="/services"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-lg px-8 py-4 font-heading font-medium text-base hover:bg-white/20 transition-colors"
            >
              Découvrir nos soins <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services highlights */}
      <section className="px-6 lg:px-8 -mt-12 relative z-10 pb-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.slice(0, 4).map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <Link
                to={`/services/${s.slug}`}
                onClick={() => window.scrollTo(0, 0)}
                className="block p-6 rounded-xl border border-border bg-card shadow-lg hover:shadow-xl hover:border-primary/30 transition-all group"
              >
                <s.icon size={22} className="text-accent mb-3" />
                <h2 className="font-heading text-base font-bold text-foreground">{s.title}</h2>
                <p className="mt-1 text-xs text-muted-foreground font-body line-clamp-2">{s.shortDesc}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Chiffres clés */}
      <section className="px-6 lg:px-8 py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {stats.map((s) => (
            <div key={s.label} className="text-center p-4 md:p-6 rounded-xl border border-border bg-card">
              <p className="font-heading text-2xl md:text-3xl font-bold text-foreground">{s.value}</p>
              <p className="text-xs md:text-sm text-muted-foreground mt-1.5 font-body">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Formules */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-accent mb-2">Nos formules</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Formules avantageuses</h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-xl mx-auto font-body">Profitez de nos combinaisons de soins à tarifs préférentiels.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {formules.map((f) => (
              <div key={f.name} className="p-6 rounded-xl border border-border bg-background hover:border-primary/30 transition-colors">
                <h3 className="font-heading text-base font-bold text-foreground">{f.name}</h3>
                <p className="text-xs text-muted-foreground mt-2 font-body leading-relaxed">{f.description}</p>
                <div className="mt-4 flex items-baseline justify-between">
                  <span className="font-heading text-xl font-bold text-accent">{f.price}</span>
                  <span className="text-xs text-muted-foreground font-body">{f.duration}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              Réserver une formule <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* À propos teaser */}
      <section className="bg-petrol">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24 w-full">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-petrol-foreground">À propos de Body by CC</h2>
            <p className="mt-4 text-sm md:text-base text-petrol-foreground/70 leading-relaxed font-body">
              Fondé par Carine Tchuente, infirmière diplômée spécialisée en esthétique médicale, Body by CC est bien plus qu'un institut de beauté. C'est un lieu dédié au bien-être, à la confiance en soi et à l'acceptation de son corps.
            </p>
            <p className="mt-3 text-sm md:text-base text-petrol-foreground/70 leading-relaxed font-body">
              Équipé de machines dernier cri — HIFU, E-SLIM Cryo 360° — chaque soin est une expérience unique, réalisée avec bienveillance et rigueur médicale.
            </p>
            <Link
              to="/a-propos"
              onClick={() => window.scrollTo(0, 0)}
              className="mt-6 inline-flex items-center gap-2 text-primary font-heading font-medium text-base hover:underline"
            >
              En savoir plus <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <ReviewsSection />

      {/* FAQ */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground text-center mb-10">Questions fréquentes</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`faq-${idx}`} className="border border-border rounded-2xl px-6 md:px-8 bg-card data-[state=open]:border-primary/30 data-[state=open]:shadow-sm">
                <AccordionTrigger className="font-heading text-base md:text-lg font-semibold text-foreground hover:no-underline py-5">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-5 font-body">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link
              to="/faq"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base md:text-lg hover:underline"
            >
              Voir toutes les questions <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Blog preview */}
      {recentArticles.length > 0 && (
        <section className="px-6 lg:px-8 py-16 md:py-20 bg-secondary/30">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-accent mb-2">Blog</p>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Nos derniers articles</h2>
                <p className="text-sm text-muted-foreground mt-2 font-body">Conseils beauté, nouveautés et astuces de nos expertes.</p>
              </div>
              <Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary text-primary font-heading font-medium text-sm hover:bg-primary hover:text-primary-foreground transition-colors shrink-0">
                Voir tous les articles <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {recentArticles.map((article, i) => (
                <BlogCard key={article.slug} article={article} index={i} animate />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Nous trouver */}
      <ContactSection />

      {/* CTA */}
      <CTASection
        title="Prêt(e) à prendre soin de vous ?"
        subtitle="Prenez rendez-vous en ligne et offrez-vous un moment de bien-être chez Body by CC."
        buttonLabel="Prendre rendez-vous"
        buttonLink={PLANITY_URL}
        isExternal
      />
    </>
  );
};

export default Index;